import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, FileText, Info, Trash2 } from "lucide-react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import {
  type AgendaItem,
  getGetJobByIdQueryKey,
  getListAgendaItemsQueryKey,
  useCreateEncounter,
  useListAgendaItems,
} from "@elation/api-client/src/stitch-web";

import {
  reconcileOrderedAgendaItems,
  useAgendaQueryCacheSync,
  useAgendaReorder,
} from "@src/features/agenda/application";
import {
  AGENDA_ITEM_DESCRIPTION_MAX_LENGTH,
  AgendaItemEntity,
  type AgendaItemProgressState,
  countAddressedAgendaProgress,
  formatAddressedAgendaProgressLabel,
  mergeActionableReorderIntoFullList,
} from "@src/features/agenda/domain";
import { AgendaProgressBadge } from "@src/features/agenda/ui/components/v2/AgendaProgressBadge";
import { DocumentViewer } from "@src/features/documents";
import {
  useTourGuide,
  VISIT_TOUR_ENCOUNTER_ID,
  VISIT_TOUR_ID,
  VISIT_TOUR_STEP_IDS,
  VisitTour,
} from "@src/features/tourguide";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@src/shared/components/ui/alert";
import { Button } from "@src/shared/components/ui/button";
import { Checkbox } from "@src/shared/components/ui/checkbox";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/shared/components/ui/tooltip";
import { InlineEditableText } from "@src/shared/components/v2/InlineEditableText";
import { cn } from "@src/shared/lib/utils";
import { navigationV2 } from "@src/shared/navigation/v2";
import { events } from "@src/shared/utils/observability/events";

import { AgendaAddItemControls } from "../AgendaAddItemControls/AgendaAddItemControls";
import { AgendaDock } from "../AgendaDock";
import {
  AgendaGroupedSortableList,
  type AgendaGroupedSortableListProps,
} from "../AgendaGroupedSortableList";
import {
  type GapListUploadHandOffContext,
  GapListUploadRowV2,
} from "../GapListUploadRow/GapListUploadRow";
import {
  type AgendaItemMutationError,
  isPendingAgendaItem,
  useAgendaItemMutations,
} from "./useAgendaItemMutations";
import {
  type AgendaGapListUploadsControl,
  useGapListUploads,
} from "./useGapListUploads";

export interface AgendaV2Props {
  encounterId?: number;
  patientId?: number;
  /**
   * When provided, gap list uploads are owned by the parent (e.g. a page-level
   * dropzone) instead of this agenda instance.
   */
  gapListUploads?: AgendaGapListUploadsControl;
  /** Used in the pre-visit agenda subtitle (“during {name}'s visit”). */
  patientFirstName?: string;
  disabled?: boolean;
  /** When true, show Ed-style addressed progress (`Agenda • X/Y addressed`). */
  visitStarted?: boolean;
  className?: string;
  /**
   * Live transcript content for the dock's transcript panel. Passed in by
   * the page (features/encounters) rather than imported here, so
   * features/agenda never depends on features/encounters. Omit to hide the
   * transcript toggle entirely (e.g. no live recording).
   */
  transcript?: ReactNode;
  /** Live line count shown in the dock's collapsed transcript toggle. */
  transcriptLineCount?: number;
}

const AGENDA_INFO_TOOLTIP =
  "Add items by typing, speaking, or dropping a gap list. They can be checked off during the visit.";

function preVisitAgendaDescription(patientFirstName?: string): string {
  const visitPhrase =
    patientFirstName != null && patientFirstName.trim() !== ""
      ? `${patientFirstName.trim()}'s visit`
      : "the visit";
  return `Care gaps grouped by health concern, with the source each came from. They check off live as you talk during ${visitPhrase}.`;
}

function sameItemsByReference(
  left: readonly AgendaItem[],
  right: readonly AgendaItem[],
): boolean {
  return (
    left.length === right.length && left.every((item, i) => item === right[i])
  );
}

function sameItemIdOrder(
  left: readonly AgendaItem[],
  right: readonly AgendaItem[],
): boolean {
  return (
    left.length === right.length &&
    left.every((item, index) => item.id === right[index]?.id)
  );
}

function sameIdSet(left: readonly number[], right: readonly number[]): boolean {
  if (left.length !== right.length) return false;
  const rightIds = new Set(right);
  return left.every((id) => rightIds.has(id));
}

function agendaReorderDisabledReason(options: {
  hasPendingCreates: boolean;
  hasIdDivergence: boolean;
  isLocalWriteActive: boolean;
}): string | undefined {
  if (options.hasPendingCreates) {
    return "Reordering is unavailable while a new agenda item is being saved.";
  }
  if (options.hasIdDivergence) {
    return "Reordering is unavailable while the agenda list is syncing.";
  }
  if (options.isLocalWriteActive) {
    return "Reordering is unavailable while saving the new order.";
  }
  return undefined;
}

interface OrderedAgendaItemsState {
  encounterId: number | undefined;
  items: AgendaItem[];
}

function mutationErrorMessage(error: AgendaItemMutationError): string {
  if (error.status === 403) {
    return "You do not have permission to update this agenda.";
  }

  if (error.status === 404) {
    return "This agenda item no longer exists.";
  }

  if (error.verb === "create") {
    return "Unable to add the agenda item. Please try again.";
  }

  if (error.verb === "delete") {
    return "Unable to remove the agenda item. Please try again.";
  }

  return "Unable to update the agenda item. Please try again.";
}

function AgendaListSkeleton() {
  return (
    <div className="px-[18px] py-4" aria-hidden="true">
      {Array.from({ length: 4 }).map((_, index) => (
        <div
          className="flex min-h-10 items-center gap-3 border-border border-b py-2"
          key={index}
        >
          <Skeleton className="size-5 rounded-sm" />
          <Skeleton className="size-4 rounded-full" />
          <Skeleton className="h-4 flex-1" />
        </div>
      ))}
    </div>
  );
}

/**
 * Tint carried by the checkbox for states the matcher has moved but not
 * closed. Restores v1's cue that an item is being worked on — without it a
 * discussed or assessed item is pixel-identical to an untouched one, which
 * reads as "the AI is doing nothing" for most of a visit.
 */
const PROGRESS_CHECKBOX_CLASSES: Partial<
  Record<AgendaItemProgressState, string>
> = {
  discussed: "border-muted-foreground/60 bg-muted",
  assessed: "border-muted-foreground/60 bg-muted",
  follow_up: "border-accent text-accent",
};

interface AgendaRowProps {
  item: AgendaItem;
  canEdit: boolean;
  onToggle: (item: AgendaItem) => void;
  onDelete: (item: AgendaItem) => void;
  onRename: (item: AgendaItem, description: string) => void;
}

function AgendaRow({
  item,
  canEdit,
  onToggle,
  onDelete,
  onRename,
}: AgendaRowProps) {
  const isCompleted = AgendaItemEntity.isCompleted(item);
  const progressState = AgendaItemEntity.progressState(item);
  const isPending = isPendingAgendaItem(item.id);
  const isInteractive = canEdit && !isPending;
  const citation = (item.citations ?? []).find(
    (entry) => entry.documentId != null,
  );

  return (
    <div
      className="group flex min-w-0 items-center gap-3 pr-1"
      data-signal-state={item.signalState}
    >
      <Checkbox
        aria-label={`Mark ${item.description} ${
          isCompleted ? "incomplete" : "complete"
        }`}
        checked={isCompleted}
        className={cn(
          "size-[17px] rounded-full border-muted-foreground/40 shadow-none data-[state=checked]:border-success data-[state=checked]:bg-success data-[state=checked]:text-success-foreground",
          PROGRESS_CHECKBOX_CLASSES[progressState],
        )}
        disabled={!isInteractive}
        onCheckedChange={() => onToggle(item)}
      />
      <InlineEditableText
        className="min-w-0 flex-1"
        disabled={!isInteractive}
        maxLength={AGENDA_ITEM_DESCRIPTION_MAX_LENGTH}
        onSave={(description) => onRename(item, description)}
        textClassName={cn(
          "truncate text-[14px] leading-5 text-foreground",
          isCompleted
            ? "text-muted-foreground line-through decoration-muted-foreground/60"
            : null,
          isPending ? "text-muted-foreground" : null,
        )}
        value={item.description}
      />
      <AgendaProgressBadge item={item} />
      {citation?.documentId != null ? (
        <DocumentViewer
          documentId={citation.documentId}
          fileName={citation.fileName ?? "Source document"}
          triggerTooltip={citation.fileName ?? "Open source document"}
          trigger={
            <Button
              aria-label={`Open source for ${item.description}`}
              className="size-7 shrink-0 text-muted-foreground"
              size="icon"
              type="button"
              variant="ghost"
            >
              <FileText aria-hidden="true" className="size-3.5" />
            </Button>
          }
        />
      ) : null}
      <Button
        aria-label={`Remove ${item.description}`}
        className="size-7 shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
        disabled={!isInteractive}
        onClick={() => onDelete(item)}
        size="icon"
        type="button"
        variant="ghost"
      >
        <Trash2 aria-hidden="true" className="size-3.5" />
      </Button>
    </div>
  );
}

export function AgendaV2({
  className,
  disabled,
  encounterId,
  gapListUploads,
  patientId,
  patientFirstName,
  visitStarted,
  transcript,
  transcriptLineCount,
}: AgendaV2Props) {
  const location = useLocation();
  const visitTour = useTourGuide(VISIT_TOUR_ID);
  const isVisitTourRoute =
    location.pathname === navigationV2.visit(VISIT_TOUR_ENCOUNTER_ID);
  const shouldUseVisitTourEncounter =
    encounterId == null &&
    visitTour.isActive &&
    (visitTour.activeStepId !== VISIT_TOUR_STEP_IDS.createPatient ||
      isVisitTourRoute);

  if (shouldUseVisitTourEncounter) {
    return (
      <AgendaV2WithGapListUploads
        className={className}
        disabled={disabled}
        encounterId={VISIT_TOUR_ENCOUNTER_ID}
        key="tour"
        patientFirstName={patientFirstName}
        patientId={patientId}
        visitStarted={visitStarted}
        transcript={transcript}
        transcriptLineCount={transcriptLineCount}
      />
    );
  }

  if (gapListUploads != null) {
    return (
      <AgendaV2View
        className={className}
        disabled={disabled}
        encounterId={encounterId}
        gapListUploads={gapListUploads}
        key="real"
        patientFirstName={patientFirstName}
        patientId={patientId}
        resetGapListUploadsOnMount={false}
        visitStarted={visitStarted}
        transcript={transcript}
        transcriptLineCount={transcriptLineCount}
      />
    );
  }

  return (
    <AgendaV2WithGapListUploads
      className={className}
      disabled={disabled}
      encounterId={encounterId}
      key="real"
      patientFirstName={patientFirstName}
      patientId={patientId}
      visitStarted={visitStarted}
      transcript={transcript}
      transcriptLineCount={transcriptLineCount}
    />
  );
}

function AgendaV2WithGapListUploads(props: AgendaV2Props) {
  const gapListUploads = useGapListUploads({
    patientId: props.patientId,
    encounterId: props.encounterId,
  });

  return <AgendaV2View {...props} gapListUploads={gapListUploads} />;
}

type AgendaV2ViewProps = AgendaV2Props & {
  gapListUploads: AgendaGapListUploadsControl;
  resetGapListUploadsOnMount?: boolean;
};

function AgendaV2View({
  encounterId,
  gapListUploads: gapListUploadsControl,
  patientId,
  patientFirstName,
  disabled = false,
  visitStarted = false,
  className,
  transcript,
  transcriptLineCount,
  resetGapListUploadsOnMount = true,
}: AgendaV2ViewProps) {
  const queryClient = useQueryClient();
  const { mutateAsync: createEncounter } = useCreateEncounter();
  const completedGapListJobsRef = useRef<Set<string> | null>(null);
  if (completedGapListJobsRef.current === null) {
    completedGapListJobsRef.current = new Set();
  }
  const completedGapListJobs = completedGapListJobsRef.current;
  const [orderedAgendaItems, setOrderedAgendaItems] =
    useState<OrderedAgendaItemsState>({
      encounterId: undefined,
      items: [],
    });
  const {
    uploads: gapListUploads,
    uploadFiles: uploadGapListFiles,
    removeUpload: removeGapListUpload,
    resetUploads: resetGapListUploads,
    isUploading: isGapListUploading,
  } = gapListUploadsControl;
  const canLoadAgenda = encounterId != null;
  const canEdit = !disabled && encounterId != null && patientId != null;
  const { scheduleReorder, isLocalReorderActive } =
    useAgendaReorder(encounterId);
  // Tracks concern-changing PATCHes in flight for a cross-group drag, keyed
  // by item id -> the new (not-yet-persisted) `healthConcernId`. A
  // cross-group move needs two server writes (concern PATCH, then position
  // PUT) but `isLocalReorderActive` only flips true once the PUT is
  // scheduled — from the PATCH's `.then()`, well after it starts. Without a
  // signal for "the PATCH itself is in flight", the reconcile effect below
  // treats the PATCH's own optimistic cache write as authoritative incoming
  // order and discards the local drop position. A Map (rather than a single
  // boolean/counter) survives overlapping writes — a second cross-group
  // drag starting before the first one's promise has settled gets its own
  // entry, so the first write's `.finally()` only ever clears its own key —
  // and doubles as the per-item overlay applied below: a background
  // refetch/poll landing while the PATCH is still in flight returns
  // server-truth data that hasn't caught up to this optimistic change yet,
  // and reconcile's `preservePersistedOrder` branch trusts incoming
  // *content* unconditionally (it only preserves order), so without the
  // overlay that stale content would silently replace the dragged item's
  // already-correct concern.
  const [pendingConcernChanges, setPendingConcernChanges] = useState<
    Map<number, number | null>
  >(() => new Map());
  const isConcernWritePending = pendingConcernChanges.size > 0;
  const isLocalWriteActive = isLocalReorderActive || isConcernWritePending;
  const agendaItemsQuery = useListAgendaItems(encounterId ?? 0, {
    query: {
      enabled: canLoadAgenda,
      refetchInterval:
        encounterId != null && encounterId > 0 && !isLocalWriteActive
          ? 5000
          : false,
    },
  });
  const fetchedItems = useMemo(
    () => agendaItemsQuery.data?.data.items ?? [],
    [agendaItemsQuery.data],
  );
  // Overlays any in-flight (not-yet-persisted) concern change onto the raw
  // server snapshot before it reaches reconcile. `preservePersistedOrder`
  // only preserves local ORDER — it still trusts incoming CONTENT
  // unconditionally by id — so a background refetch landing mid-PATCH
  // (periodic poll, SSE-driven invalidate) would otherwise silently
  // overwrite the dragged item's already-correct concern with the
  // server's stale, pre-PATCH value.
  const reconcileSourceItems = useMemo(() => {
    if (pendingConcernChanges.size === 0) return fetchedItems;
    return fetchedItems.map((item) => {
      if (!pendingConcernChanges.has(item.id)) return item;
      return {
        ...item,
        healthConcernId: pendingConcernChanges.get(item.id) ?? null,
        // Mirrors `getOptimisticAgendaItemPatch`'s `concernPatch`:
        // `concernGroupKey` prefers the nested `healthConcern` over the
        // flat id, so a stale nested object here would win over the
        // override above.
        healthConcern: null,
      };
    });
  }, [fetchedItems, pendingConcernChanges]);
  const actionableFetchedItems = useMemo(
    () => fetchedItems.filter(AgendaItemEntity.isActionable),
    [fetchedItems],
  );
  const {
    createAgendaItem,
    toggleAgendaItem,
    updateAgendaItemDescription,
    updateAgendaItemConcern,
    deleteAgendaItem,
    error,
    pendingCreateIds,
    completedCreateIds,
  } = useAgendaItemMutations({ encounterId, patientId });
  useAgendaQueryCacheSync(encounterId);
  const pendingCreateIdSet = useMemo(
    () => new Set<number>(pendingCreateIds),
    [pendingCreateIds],
  );
  const hasPendingCreates = pendingCreateIds.length > 0;
  const orderedItems =
    orderedAgendaItems.encounterId === encounterId &&
    (orderedAgendaItems.items.length > 0 || actionableFetchedItems.length === 0)
      ? orderedAgendaItems.items
      : actionableFetchedItems;
  const localPersistedActionableIds = useMemo(
    () =>
      orderedItems
        .filter((item) => !isPendingAgendaItem(item.id))
        .map((item) => item.id),
    [orderedItems],
  );
  const snapshotActionableIds = useMemo(
    () => actionableFetchedItems.map((item) => item.id),
    [actionableFetchedItems],
  );
  const hasIdDivergence = !sameIdSet(
    localPersistedActionableIds,
    snapshotActionableIds,
  );
  const canReorder = canEdit && !hasPendingCreates && !hasIdDivergence;
  const reorderDisabledReason = agendaReorderDisabledReason({
    hasPendingCreates,
    hasIdDivergence,
    isLocalWriteActive,
  });

  useEffect(() => {
    setOrderedAgendaItems((current) => {
      const currentItems =
        current.encounterId === encounterId ? current.items : [];
      const next = reconcileOrderedAgendaItems(
        currentItems,
        reconcileSourceItems,
        pendingCreateIdSet,
        completedCreateIds,
        // The actual fix: while a concern PATCH is in flight (cross-group
        // drag), preserve local drop order the same way a scheduled reorder
        // does. Without this, the PATCH's `onMutate` cache write is treated
        // as authoritative incoming order and the dragged item's position
        // reverts to wherever it sits in the untouched cache array.
        { preservePersistedOrder: isLocalWriteActive },
      );

      if (
        current.encounterId === encounterId &&
        sameItemIdOrder(current.items, next) &&
        sameItemsByReference(current.items, next)
      ) {
        return current;
      }

      return { encounterId, items: next };
    });
  }, [
    encounterId,
    reconcileSourceItems,
    pendingCreateIdSet,
    completedCreateIds,
    isLocalWriteActive,
  ]);

  useEffect(() => {
    if (
      !hasIdDivergence ||
      encounterId == null ||
      isLocalWriteActive ||
      hasPendingCreates
    ) {
      return;
    }

    void queryClient.invalidateQueries({
      queryKey: getListAgendaItemsQueryKey(encounterId),
    });
  }, [
    encounterId,
    hasIdDivergence,
    hasPendingCreates,
    isLocalWriteActive,
    queryClient,
  ]);

  useEffect(() => {
    if (!resetGapListUploadsOnMount) return;

    completedGapListJobs.clear();
    resetGapListUploads();
  }, [
    resetGapListUploads,
    resetGapListUploadsOnMount,
    completedGapListJobs.clear,
  ]);

  const handleRemoveGapListUpload = useCallback(
    (uploadId: string) => {
      removeGapListUpload(uploadId);
    },
    [removeGapListUpload],
  );

  const handleGapListJobComplete = useCallback(
    async ({
      jobId,
      patientId: uploadPatientId,
      encounterId: uploadEncounterId,
    }: GapListUploadHandOffContext) => {
      if (completedGapListJobs.has(jobId)) return;
      completedGapListJobs.add(jobId);

      const jobData = queryClient.getQueryData(getGetJobByIdQueryKey(jobId)) as
        | { data?: { metadata?: { opportunityIds?: number[] } } }
        | undefined;
      const opportunityIds = jobData?.data?.metadata?.opportunityIds ?? [];

      try {
        // Same hand-off v1 uses when opening Today: createEncounter runs
        // assignToEncounter for any open, unassigned agenda items.
        const encounterResult = await createEncounter({
          data: { patientId: uploadPatientId },
        });
        const assignedEncounterId = encounterResult.data.id;

        await queryClient.invalidateQueries({
          queryKey: getListAgendaItemsQueryKey(assignedEncounterId),
        });

        const routeEncounterId = uploadEncounterId ?? encounterId;
        if (
          routeEncounterId != null &&
          routeEncounterId !== assignedEncounterId
        ) {
          await queryClient.invalidateQueries({
            queryKey: getListAgendaItemsQueryKey(routeEncounterId),
          });
        }

        events.careGapsExtracted.track({
          patient_id: uploadPatientId,
          count: opportunityIds.length,
        });
      } catch (err) {
        completedGapListJobs.delete(jobId);
        throw err;
      }
    },
    [
      createEncounter,
      encounterId,
      queryClient,
      completedGapListJobs.add,
      completedGapListJobs.delete,
      completedGapListJobs.has,
    ],
  );

  const handleOrderedItemsChange = (
    actionableOrdered: AgendaItem[],
    concernChange: Parameters<
      AgendaGroupedSortableListProps["onItemsChange"]
    >[1],
  ) => {
    const cachedItems =
      encounterId != null
        ? (
            queryClient.getQueryData(getListAgendaItemsQueryKey(encounterId)) as
              | { data?: { items?: AgendaItem[] } }
              | undefined
          )?.data?.items
        : undefined;
    const fullSource =
      fetchedItems.length > 0
        ? fetchedItems
        : (cachedItems ?? actionableOrdered);
    const actionablePersisted = actionableOrdered.filter(
      (item) => !isPendingAgendaItem(item.id),
    );
    const fullProjected = mergeActionableReorderIntoFullList(
      fullSource,
      actionablePersisted,
      AgendaItemEntity.isActionable,
    );

    if (fullProjected == null) {
      if (encounterId != null) {
        void queryClient.invalidateQueries({
          queryKey: getListAgendaItemsQueryKey(encounterId),
        });
      }
      return;
    }

    setOrderedAgendaItems({ encounterId, items: actionableOrdered });

    // A cross-group move needs two writes, and there are TWO DISTINCT races
    // here with DIFFERENT symptoms. Both are guarded; don't collapse them.
    //
    // (1) Ordering of the writes. The concern PATCH must land BEFORE the
    // position-only reorder: `useAgendaReorder`'s mutation always
    // invalidates the agenda query on settle, and a refetch landing before
    // this PATCH completes would revert the item's concern while keeping
    // its new position — which relocates the "Added by you" HEADER instead
    // of the item, since `groupByConcern` anchors a group to the array
    // position of its first member. Awaiting the PATCH below is that guard.
    //
    // (2) Local order surviving the PATCH round trip. Fixing (1) opened a
    // second window with a different symptom: the item lands in the CORRECT
    // group but at the WRONG POSITION WITHIN IT (the header does not move).
    // `isLocalReorderActive` only flips true once `scheduleReorder` runs —
    // from the `.then()` below — so for the whole round trip the reconcile
    // effect would otherwise run with `preservePersistedOrder: false` and
    // discard the drop position, while the PATCH's own `onMutate` cache
    // write re-triggers it. `pendingConcernChanges` (folded into
    // `isLocalWriteActive` above) is that guard.
    //
    // It also carries the new concern per item, because
    // `preservePersistedOrder` only preserves ORDER — reconcile still
    // substitutes incoming CONTENT by id, so a poll landing mid-PATCH would
    // otherwise overwrite the optimistic concern with stale server truth.
    // That overlay is applied in `reconcileSourceItems`.
    //
    // `.finally()` clears the entry on both success and failure so it can
    // never get stuck — a stuck entry would permanently disable
    // drag-and-drop for that item.
    if (concernChange != null) {
      setPendingConcernChanges((current) => {
        const next = new Map(current);
        next.set(concernChange.itemId, concernChange.healthConcernId);
        return next;
      });
      void updateAgendaItemConcern(
        concernChange.itemId,
        concernChange.healthConcernId,
      )
        .then(() => scheduleReorder(fullProjected))
        .catch(() => {
          if (encounterId != null) {
            void queryClient.invalidateQueries({
              queryKey: getListAgendaItemsQueryKey(encounterId),
            });
          }
        })
        .finally(() => {
          setPendingConcernChanges((current) => {
            if (!current.has(concernChange.itemId)) return current;
            const next = new Map(current);
            next.delete(concernChange.itemId);
            return next;
          });
        });
      return;
    }

    scheduleReorder(fullProjected);
  };

  const handleGapListFilesSelected = (files: File[]) => {
    uploadGapListFiles(files);
  };

  const handleToggle = (item: AgendaItem) => {
    if (!canEdit || isPendingAgendaItem(item.id)) return;
    void toggleAgendaItem(item.id, item.status, item.signalState);
  };

  const handleDelete = (item: AgendaItem) => {
    if (!canEdit || isPendingAgendaItem(item.id)) return;
    void deleteAgendaItem(item.id);
  };

  const handleRename = (item: AgendaItem, description: string) => {
    if (!canEdit || isPendingAgendaItem(item.id)) return;
    void updateAgendaItemDescription(item.id, description);
  };

  const hasDisplayableRows =
    orderedItems.length > 0 || gapListUploads.length > 0;
  const showInitialLoading = canLoadAgenda && agendaItemsQuery.isLoading;
  const showBlockingError =
    canLoadAgenda && agendaItemsQuery.isError && !hasDisplayableRows;
  const showRefreshError =
    canLoadAgenda && agendaItemsQuery.isError && hasDisplayableRows;
  const addressedProgress = countAddressedAgendaProgress(orderedItems);
  const addressedProgressLabel =
    formatAddressedAgendaProgressLabel(addressedProgress);
  const emptyMessage = canEdit
    ? "No items yet. Add your first below, or speak / drop a gap list."
    : patientId == null
      ? "No items yet. Select a patient first, then add agenda items."
      : "No items yet. Start a visit before adding agenda items.";

  return (
    <section
      aria-label="Visit agenda"
      className={cn(
        "relative flex min-h-0 flex-1 flex-col bg-background",
        className,
      )}
    >
      <VisitTour.AgendaItemsStep className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="px-[18px] pt-4">
            {visitStarted ? (
              <h1
                className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground"
                aria-label={`${addressedProgress.addressed} of ${addressedProgress.total} agenda items addressed`}
              >
                {addressedProgressLabel}
              </h1>
            ) : (
              <>
                <div className="flex items-center gap-1.5">
                  <h1 className="text-[16px] font-semibold tracking-[-0.02em] text-foreground">
                    Visit agenda
                  </h1>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label="About visit agenda"
                          className="size-6 rounded-full text-muted-foreground hover:bg-transparent hover:text-foreground"
                        >
                          <Info className="size-4" aria-hidden="true" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-[260px] leading-5"
                      >
                        {AGENDA_INFO_TOOLTIP}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {preVisitAgendaDescription(patientFirstName)}
                </p>
              </>
            )}
          </div>

          {showInitialLoading ? (
            <AgendaListSkeleton />
          ) : showBlockingError ? (
            <div className="px-[18px] py-4">
              <Alert variant="destructive">
                <AlertCircle aria-hidden="true" className="size-4" />
                <AlertTitle>Unable to load agenda</AlertTitle>
                <AlertDescription>
                  Refresh the page or try again in a moment.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <div className="px-[18px] py-2">
              {error ? (
                <Alert className="mb-3" variant="destructive">
                  <AlertCircle aria-hidden="true" className="size-4" />
                  <AlertTitle>Agenda update failed</AlertTitle>
                  <AlertDescription>
                    {mutationErrorMessage(error)}
                  </AlertDescription>
                </Alert>
              ) : null}
              {showRefreshError ? (
                <Alert className="mb-3" variant="destructive">
                  <AlertCircle aria-hidden="true" className="size-4" />
                  <AlertTitle>Agenda refresh failed</AlertTitle>
                  <AlertDescription>
                    Showing the most recent agenda items. We will try again
                    automatically.
                  </AlertDescription>
                </Alert>
              ) : null}

              {orderedItems.length > 0 ? (
                <AgendaGroupedSortableList
                  className="bg-background"
                  disabled={!canReorder || isLocalWriteActive}
                  items={orderedItems}
                  onItemsChange={handleOrderedItemsChange}
                  reorderDisabledReason={reorderDisabledReason}
                  renderItem={(item) => (
                    <AgendaRow
                      canEdit={canEdit}
                      item={item}
                      onDelete={handleDelete}
                      onRename={handleRename}
                      onToggle={handleToggle}
                    />
                  )}
                />
              ) : gapListUploads.length === 0 ? (
                <p className="py-4 text-sm text-muted-foreground">
                  {emptyMessage}
                </p>
              ) : null}

              {gapListUploads.length > 0 ? (
                <ul aria-label="Gap list uploads" className="mt-3">
                  {gapListUploads.map((upload) => (
                    <GapListUploadRowV2
                      key={`${upload.id}:${upload.jobId ?? "pending"}`}
                      onComplete={handleGapListJobComplete}
                      onRemove={handleRemoveGapListUpload}
                      upload={upload}
                    />
                  ))}
                </ul>
              ) : null}
            </div>
          )}
        </div>
      </VisitTour.AgendaItemsStep>

      {/*
       * Reference prototype (`visit.tsx:846-931`) pins the composer above
       * the scratch pad / transcript dock, not below it — swapped here for
       * STITCH-551. See `AgendaAddItemControls`' and `AgendaDock`'s own
       * comments for how the border/shadow/bottom-padding seams were
       * reconciled for the new order.
       */}
      <AgendaAddItemControls
        canEdit={canEdit}
        encounterId={encounterId}
        isGapListUploading={isGapListUploading}
        patientId={patientId}
        onCreateAgendaItem={createAgendaItem}
        onGapListFilesSelected={handleGapListFilesSelected}
      />

      <AgendaDock
        disabled={!canEdit}
        encounterId={encounterId}
        transcript={transcript}
        transcriptLineCount={transcriptLineCount}
      />
    </section>
  );
}
